import { useRef, useState, type RefObject } from 'react'
import { emit } from '../../platform/analytics/analytics'
import { Button } from '../../platform/primitives/Button'
import { ConfirmDialog } from '../../platform/primitives/Dialog'
import { ListRow } from '../../platform/primitives/ListRow'
import { PersonAvatar } from '../../platform/primitives/PersonAvatar'
import { InviteLink } from './InviteLink'
import { ROLE_LABELS } from './roles'
import { describeResendFailure } from './rowFailure'
import { daysSince, isExpired, type Invitation } from './useInvitations'
import { useRowMutation } from './useRowMutation'
import './household.css'

/** en-GB, matching `MemberRow`'s own date formatting and `Async`'s
 *  stale-date formatting — the one convention this app renders bare dates
 *  with. */
const DATE = new Intl.DateTimeFormat('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })

/**
 * One pending (or expired, or otherwise resolved) invitation.
 *
 * **Resend and revoke are plain callback props here, not mutations this
 * component owns.** Task 8 renders the row only; the confirmation dialog on
 * revoke and the rotated-link handling on resend are Task 10's, which is
 * expected to change what `onResend`/`onRevoke` DO without changing this
 * row's shape.
 *
 * `sublabel` stays empty, unlike `MemberRow`'s: an invitation has one
 * identifier (the email, which is already the `label`), never a second.
 *
 * **`onResend`/`onRevoke` are optional, and a caller supplying neither gets
 * no actions rendered at all** — not two buttons wired to do nothing. Task
 * 8 has no real mutation to attach yet (Task 10's), and a live-looking
 * button that silently no-ops when clicked is worse than an absent one.
 * This is the same shape `MemberRow` shipped with in Task 4: no actions,
 * not dead ones.
 *
 * **Expired gets a distinct WORD, not only a colour** (M0-R11). The pending
 * line reads "Sent N days ago · expires DATE"; the expired line drops "Sent…
 * ago" (which stopped being true the moment it expired) for "Expired DATE" —
 * the word survives even without colour vision, and `--neg` is added only as
 * reinforcement.
 *
 * **`expired` is derived, not trusted from `status`** — via `isExpired`
 * (`useInvitations.ts`), exported rather than local for the same reason
 * `daysSince` is: Task 9's `inviteBlockedReason` needs the identical
 * derivation, and a second implementation would drift from this one.
 */
export function InvitationRow({
  invitation,
  onResend,
  onRevoke,
  restoreFocusTo,
}: {
  invitation: Invitation
  /**
   * Resolves with the ROTATED invitation — M12-R8. Not `void`, because the
   * server rotates the token on this same row and the new one is the only
   * thing the inviter can act on; the list endpoint never returns a token, so
   * this response is the second and last moment it is readable.
   */
  onResend?: (id: string) => Promise<Invitation>
  onRevoke?: (id: string) => Promise<void>
  /**
   * Where focus goes after a confirmed revoke, and it must be OUTSIDE this row.
   *
   * Revoking removes the invitation from a list of pending ones, so this row
   * unmounts — including the Revoke button that opened the confirmation. A
   * `restoreFocusTo` pointing at that button is a detached node by the time
   * `Overlay` restores, `focusFirstAlive` skips it, and focus lands on
   * `<body>`. Measured in a browser: with the refetched row forced to stay,
   * focus restored correctly; left to unmount, it did not.
   *
   * That is the same defect `InviteDialog` hit in #316 — a trigger whose own
   * success path destroys it — reached from the opposite direction, and jsdom
   * cannot see either.
   */
  restoreFocusTo?: RefObject<HTMLElement | null>
}) {
  /**
   * The invitation as the SERVER last described it, which is not the prop after
   * a resend: the refetch that would update the prop is a round trip behind,
   * and it will not carry a token when it lands.
   */
  const [resent, setResent] = useState<Invitation | null>(null)
  /**
   * In flight for THIS row, and deliberately not the hook's `isPending` —
   * `useRowMutation`'s own docblock states the arity argument once
   * (`useResendInvitation` is called ONCE in `Household.tsx` for the whole
   * list, so its `isPending` is a single flag shared by every row: one resend
   * would disable all N buttons, and the row that reported "sending" would
   * not be the row that was).
   *
   * Without any guard, two clicks send two rotations. The server rotates the
   * token on each, so the FIRST response carries a token the second has already
   * invalidated — and responses can settle in either order, leaving the row
   * presenting a dead link under "share this one instead". That is the precise
   * failure this feature exists to prevent, arrived at from the inside —
   * `useRowMutation`'s in-flight dedupe is what closes it now.
   */
  const resendMutation = useRowMutation(
    (id: string) => (onResend ? onResend(id) : Promise.reject(new Error('no onResend supplied'))),
    (cause) => describeResendFailure(cause),
  )
  const [confirming, setConfirming] = useState(false)
  const revokeButton = useRef<HTMLButtonElement>(null)
  const roleLabel = invitation.role ? ROLE_LABELS[invitation.role] : invitation.rawRole
  const expired = isExpired(invitation)
  const days = daysSince(invitation.createdAt)
  const expiresOn = DATE.format(new Date(invitation.expiresAt))
  // "Sent 0 days ago" is what every invitation reads seconds after being
  // sent — `daysSince` floors at zero, correctly, but the row needs its own
  // wording for that case rather than borrowing the analytics value's shape.
  const sentPhrase = days === 0 ? 'Sent today' : `Sent ${days} day${days === 1 ? '' : 's'} ago`
  const timing = expired ? `Expired ${expiresOn}` : `${sentPhrase} · expires ${expiresOn}`

  return (
    // A wrapper, because `ListRow` renders a `<div>` and takes no children:
    // the rotated link and the revoke confirmation belong WITH this row, not
    // loose among its siblings. No list semantics are harmed — the panel's
    // rows have never been `<li>`s.
    <div className="invitation-row__group">
    <ListRow
      label={invitation.email}
      leading={<PersonAvatar name={null} email={invitation.email} />}
      trailing={
        <span className="invitation-row__meta">
          <span className="invitation-row__role">{roleLabel}</span>
          <span
            className={
              expired ? 'invitation-row__timing invitation-row__timing--expired' : 'invitation-row__timing'
            }
          >
            {timing}
          </span>
          {(onResend || onRevoke) && (
            <span className="invitation-row__actions">
              {/* `size="panel"`, matching `Merchants.tsx`'s own row actions —
                  NOT `"inline"`. `StatesReference.test.tsx` pins the app-wide
                  property that nothing uses the 24px target yet, and this row
                  is not the dense-row exception that guard is waiting for. */}
              {onResend && (
                <Button
                  size="panel"
                  variant="outline"
                  aria-label={`Resend invitation to ${invitation.email}`}
                  // NOT `disabled={resendMutation.pending}` — a disabled focused
                  // element drops focus to `<body>` in Chromium with nothing to
                  // restore it (verified; same defect as `RoleMenu`'s, #312
                  // blocking finding 2, hit again here in #362). `aria-busy`
                  // already says the control is working; the guard below gives
                  // the same double-submit protection `disabled` was giving,
                  // without taking focus away from the user.
                  aria-busy={resendMutation.pending}
                  onClick={() => {
                    if (resendMutation.pending) return
                    // Task 12. `days` — already computed above for the row's
                    // own "Sent N days ago" text — is `daysSince(invitation.
                    // createdAt)` measured at THIS row's ORIGINAL send, the
                    // exact `invite_age_days` `vocabulary.ts` wants: how
                    // stale was this invite when someone thought to resend
                    // it. Only on a resolved resend, never on the reject
                    // `.catch` swallows below.
                    void resendMutation
                      .run(invitation.id)
                      .then((updated) => {
                        setResent(updated)
                        emit('invite_resent', { invite_age_days: days })
                      })
                      .catch(() => {})
                  }}
                >
                  {resendMutation.pending ? 'Sending…' : 'Resend'}
                </Button>
              )}
              {onRevoke && (
                <Button
                  ref={revokeButton}
                  size="panel"
                  variant="outline"
                  aria-label={`Revoke invitation to ${invitation.email}`}
                  // Opens the confirmation; it does NOT revoke. M12-R8 asks for
                  // a confirmation, and one that acts before it is confirmed is
                  // a dialog people learn to dismiss.
                  onClick={() => setConfirming(true)}
                >
                  Revoke
                </Button>
              )}
            </span>
          )}
        </span>
      }
    />
      {/* Below the row rather than inside its trailing metadata: the rotated
          link is a paragraph and a field, not a chip, and the row's trailing
          slot is a single line that the timing and role already share. */}
      {resent !== null && (
        <InviteLink
          token={resent.token}
          label={`Invite link for ${resent.email}`}
          unavailable="Resent, but the new link is unavailable here — resend again from a device that can show it."
        >
          {/* Naming the consequence, not just the event. The rotation kills the
              previous link (`invitation_service.py:535`), and per #305 that
              link is the only way this can be accepted from a browser — so
              someone who already sent the old one is now waiting on a URL that
              can never work, and nothing else on screen would say so. */}
          <p className="invitation-row__resent" role="status">
            Sent again. The previous link no longer works — share this one instead.
          </p>
        </InviteLink>
      )}
      {resendMutation.failure !== null && (
        <p className="invitation-row__failure" role="alert">
          {resendMutation.failure}
        </p>
      )}
      {onRevoke && (
        <ConfirmDialog
          open={confirming}
          mode="revoke_invitation"
          title="Revoke this invitation?"
          // The address, because two pending invitations differ only by it and
          // the row does not scroll into the dialog.
          body={`${invitation.email} will not be able to join this household with the link they were sent.`}
          confirmLabel="Revoke"
          destructive
          // The caller's anchor when it gave one — this row is about to cease
          // to exist. `revokeButton` remains the fallback for a caller that
          // keeps the row (nothing does today, but the prop is optional and a
          // detached ref is what `focusFirstAlive` is built to survive).
          restoreFocusTo={restoreFocusTo ?? revokeButton}
          onCancel={() => setConfirming(false)}
          onConfirm={async () => {
            await onRevoke(invitation.id)
            // Task 12. Same `days` the row's own timing text already reads —
            // see `Resend`'s identical comment above.
            emit('invite_revoked', { invite_age_days: days })
            setConfirming(false)
          }}
        />
      )}
    </div>
  )
}
